import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useLocalizedPrice, useUserLocale } from "../hooks/useLocalizedPrice";
import { useAuth } from "../context/AuthContext";
import { RealNameConsentModal } from "./RealNameConsentModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "결제하기" 버튼 — 사용자 위치를 자동 감지하여 결제 시 현지 통화로 PayPal Order 생성.
 * 카탈로그는 USD 기준이지만 결제 페이지에서는 KRW/USD/CAD 로 표시됨.
 * 가맹점은 CAD 계정으로 자동 정산 (PayPal 자동 환전).
 *
 * requireRealName=true 인 경우(정회원 이상 멤버십), 실명/노출 동의가 없으면
 * 결제 전에 실명 입력 모달을 띄워 동의를 받은 뒤에만 결제로 진행.
 */
export function PayPalCheckoutButton({
  planId,
  dynamicPlanId,
  amount,           // USD 카탈로그 가격
  label,
  disabled = false,
  requireRealName = false,
  testIdPrefix = "paypal",
  className,        // 버튼 커스텀 스타일 (미지정 시 기본)
}) {
  const [busy, setBusy] = useState(false);
  const [showRealName, setShowRealName] = useState(false);
  // 사용자 통화 자동 감지 (KRW/USD/CAD)
  const { currency: userCurrency } = useUserLocale();
  const { format: formatLocal, ready: fxReady } = useLocalizedPrice("USD");
  const { isAuthenticated, user } = useAuth();

  const startCheckout = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("liztv_token");
      const headers = { "Content-Type": "application/json" };
      if (token) headers.Authorization = `Bearer ${token}`;
      const body = {
        return_path: "/payment/success",
        cancel_path: "/payment/cancel",
        display_currency: userCurrency,  // 자동 감지된 사용자 통화
      };
      if (planId) body.plan_id = planId;
      if (dynamicPlanId) {
        body.plan_id = "dynamic";
        body.dynamic_plan_id = dynamicPlanId;
      }
      const res = await fetch(`${API_URL}/api/paypal/orders/create`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "결제 주문 생성에 실패했습니다.");
      }
      if (!data.approve_url) {
        throw new Error("PayPal 승인 URL을 가져오지 못했습니다.");
      }
      window.location.href = data.approve_url;
    } catch (err) {
      console.error("PayPal redirect error", err);
      toast.error(err.message || "결제를 시작할 수 없습니다.");
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (busy || disabled) return;
    if (!isAuthenticated) {
      toast.info("결제를 진행하려면 먼저 로그인 또는 회원가입해주세요.");
      window.dispatchEvent(
        new CustomEvent("openAuthModal", { detail: { mode: "login" } })
      );
      return;
    }
    // 정회원 이상 멤버십: 실명 + 노출 동의 필요
    if (requireRealName && (!(user?.real_name || "").trim() || !user?.real_name_consent)) {
      setShowRealName(true);
      return;
    }
    startCheckout();
  };

  return (
    <div className="w-full" data-testid={`${testIdPrefix}-wrapper`}>
      <RealNameConsentModal
        open={showRealName}
        onClose={() => setShowRealName(false)}
        onConfirmed={() => { setShowRealName(false); startCheckout(); }}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={busy || disabled}
        className={className || "w-full px-5 py-3 rounded-xl bg-stone-800 hover:bg-stone-900 text-white font-semibold text-sm tracking-wide transition-all duration-300 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"}
        data-testid={`${testIdPrefix}-pay-btn`}
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>PayPal로 이동 중…</span>
          </>
        ) : (
          <span>{label || "결제하기"}</span>
        )}
      </button>
      {amount ? (
        <div className="text-[11px] text-stone-400 mt-2 text-center leading-relaxed">
          USD ${amount.toLocaleString()}
          {userCurrency !== "USD" && fxReady ? (
            <span className="block text-stone-500 mt-0.5">
              결제 시 약 <span className="font-semibold text-stone-600">{formatLocal(amount)}</span> 으로 안내됩니다
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default PayPalCheckoutButton;
